import { createContext, forwardRef, useContext } from "react";
import { Box, Checkbox, Paper, PaperProps } from "@mui/material";

/**
 * A "Select all" row pinned to the top of the Parts dropdown.
 *
 * Lives in the popup rather than under the field so the list stays open
 * while picking: tick everything, untick the one or two that do not belong,
 * done. Fed through context instead of props because Autocomplete's
 * PaperComponent has to be a stable reference - an inline component
 * remounts the list on every render and loses its scroll position.
 */
export type PartsSelectAll = {
  /** How many parts a click could pick by hand. */
  total: number;
  /** How many of those are picked right now. */
  selected: number;
  /** Pick them all, or clear them all when they already are. */
  onToggle: () => void;
};

export const PartsSelectAllContext = createContext<PartsSelectAll | null>(null);

const PartsSelectAllPaper = forwardRef<HTMLDivElement, PaperProps>(
  ({ children, ...props }, ref) => {
    const selectAll = useContext(PartsSelectAllContext);
    const show = !!selectAll && selectAll.total > 1;
    const all = !!selectAll && selectAll.selected === selectAll.total;
    return (
      <Paper ref={ref} {...props}>
        {show && (
          // preventDefault on mousedown keeps focus in the input, which is
          // what keeps the popup open - Autocomplete closes on blur.
          <Box
            role="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={selectAll.onToggle}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 0.75,
              cursor: "pointer",
              userSelect: "none",
              borderBottom: 1,
              borderColor: "divider",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Checkbox
              size="small"
              sx={{ p: 0 }}
              tabIndex={-1}
              checked={all}
              indeterminate={!all && selectAll.selected > 0}
              // The row handles the click; a handler here as well would
              // toggle twice.
              readOnly
            />
            <span>{all ? "Clear all" : `Select all (${selectAll.total})`}</span>
          </Box>
        )}
        {children}
      </Paper>
    );
  }
);

PartsSelectAllPaper.displayName = "PartsSelectAllPaper";

export default PartsSelectAllPaper;
